import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, num, rupee, pnlClass } from "@/lib/api";
import { ConfirmSwitch } from "@/components/ConfirmSwitch";

const Mark = ({ ok, children }) => <span className={ok ? "up" : "text-slate-500"}>{children}</span>;
const P = ({ v, id }) => <td data-testid={id} className={pnlClass(v)}>{v ? rupee(v) : "—"}</td>;

const BudgetInput = ({ x, onSave, disabled }) => {
  const [v, setV] = useState(x.budget ?? "");
  useEffect(() => setV(x.budget ?? ""), [x.budget]);
  const save = () => { const n = Number(v) || 0; if (n !== (x.budget || 0)) onSave(x.kind, n); };
  const hit = x.budget_hit?.live || x.budget_hit?.dry;
  return (
    <div className="flex items-center justify-end gap-1">
      <span className="text-slate-500">−₹</span>
      <input data-testid={`input-budget-${x.kind}`} type="number" min={0} max={4000} step={100} placeholder="none" value={v} disabled={disabled}
        onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="h-6 w-16 rounded border border-slate-700 bg-slate-900 px-1 text-right text-[11px] text-slate-100 focus:border-indigo-500 focus:outline-none" />
      {hit && <span data-testid={`budget-hit-${x.kind}`} className="rounded bg-red-500/15 px-1 text-[9px] font-semibold text-red-400">STOPPED</span>}
    </div>
  );
};

export const LiveStrategiesPanel = ({ override, onChange, rev }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/execution/strategies").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load, rev]);
  const saveBudget = async (kind, limit) => {
    setBusy(true);
    try { setD((await api.post("/execution/budget", { kind, limit })).data); toast.success(limit ? `Daily budget set to ₹${limit}` : "Budget removed"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const preset = async (limit) => {
    setBusy(true);
    try { setD((await api.post("/execution/budget/all", { limit })).data); toast.success(limit ? `Every strategy: ₹${limit} daily budget` : "All budgets cleared"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = async (kind, enabled) => {
    setBusy(true);
    try { setD((await api.post("/execution/strategies", { kind, enabled })).data); toast.success(enabled ? "Strategy enabled for auto-trading" : "Strategy disabled"); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const fallback = d.enabled.length === 0;
  return (
    <div className="panel col-span-12 p-4" data-testid="panel-live-strategies">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="eyebrow">Auto-trading strategies · each runs on its own rules</span>
        <div className="flex flex-wrap items-center gap-1" data-testid="budget-presets">
          <span className="mr-1 text-[11px] text-slate-500">Budget for all:</span>
          {[300, 500, 1000].map((v) => (
            <button key={v} data-testid={`budget-preset-${v}`} disabled={busy} onClick={() => preset(v)}
              className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 transition-colors hover:border-indigo-500 hover:text-indigo-200 disabled:opacity-50">₹{v}</button>
          ))}
          <button data-testid="budget-preset-clear" disabled={busy} onClick={() => preset(0)}
            className="rounded px-2 py-0.5 text-[11px] text-slate-500 transition-colors hover:text-red-300 disabled:opacity-50">Clear all</button>
        </div>
        <span className="num text-[11px] text-slate-500" data-testid="live-strategies-count">{d.enabled.length} enabled · max {d.max_open} open at once</span>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Every enabled strategy is checked on each closed candle and trades by itself, in paper (auto-paper), dry run and live (when armed).
        Optional daily budget per strategy (inside the shared cap): when a strategy's own loss today reaches it, its open position is exited and only that strategy stops for the day.
        Shared limits: 1 lot each, ₹1,000 max risk per trade (₹2,000 for straddles and strangles), a ₹{num(d.hard_daily_loss, 0)} combined daily stop, at most {d.max_open} open positions, opposite trades blocked,
        and Dhan margin checked before every entry.
        {fallback && <span className="text-amber-300/90" data-testid="live-strategies-fallback"> None enabled: only the strategy picked in Settings trades.</span>}
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="dense num w-full" data-testid="table-live-strategies">
          <thead><tr><th className="!text-left">Strategy</th><th>OOS trades</th><th>OOS PF</th><th>Paper trades</th><th>Paper PF</th><th>Validation</th><th>Auto-trade</th><th>Open live</th>
            <th>Live today</th><th>Live week</th><th>Dry today</th><th>Dry week</th><th className="!text-right">Daily budget</th></tr></thead>
          <tbody>
            {d.strategies.map((x) => (
              <tr key={x.kind} data-testid={`live-strategy-row-${x.kind}`}>
                <td className="!text-left text-slate-200">{x.label}{x.kind === d.active && <span className="ml-1 text-[10px] text-indigo-300">(Settings)</span>}
                  {x.carry && <span className="block text-[10px] text-amber-400/80">NRML futures + weekly call · about ₹2.5L margin</span>}</td>
                <td><Mark ok={x.oos_trades >= 200}>{x.oos_trades}</Mark></td>
                <td><Mark ok={x.oos_trades >= 200 && (x.oos_pf || 0) >= 2}>{x.oos_pf >= 999 ? "∞" : num(x.oos_pf)}</Mark></td>
                <td><Mark ok={x.paper_trades >= 40}>{x.paper_trades}</Mark></td>
                <td><Mark ok={x.paper_trades >= 40 && (x.paper_pf || 0) >= 2}>{x.paper_pf >= 999 ? "∞" : num(x.paper_pf)}</Mark></td>
                <td data-testid={`live-strategy-validation-${x.kind}`} className={x.validated ? "up" : override ? "text-amber-400" : "down"}>
                  {x.validated ? "PASS" : override ? "OVERRIDDEN" : "FAIL · skipped live"}</td>
                <td>
                  <ConfirmSwitch id={`switch-strategy-${x.kind}`} checked={x.enabled} disabled={busy} tone={x.carry ? "amber" : undefined}
                    label={x.enabled ? "ON" : "OFF"} onOn={() => toggle(x.kind, true)} onOff={() => toggle(x.kind, false)}
                    confirmLabel={`Enable ${x.label}`} title={`Auto-trade ${x.label}?`}
                    body={x.carry
                      ? "When live trading is armed, this BUYS 1 NIFTY futures lot (NRML, held overnight) and SELLS a weekly OTM call with REAL money. Both legs close at expiry and it re-enters the next session. It is skipped if Dhan margin is short."
                      : `When live trading is armed, ${x.label} places REAL Dhan orders on its own signals: 1 lot, ${["short_straddle", "short_strangle", "long_straddle", "long_strangle"].includes(x.kind) ? "₹2,000" : "₹1,000"} max risk, sharing the ₹${num(d.hard_daily_loss, 0)} daily stop. ${x.validated ? "" : override ? "It fails validation and trades only because the override is on." : "It fails validation, so it stays paper and dry-run only until it passes or you turn on the override."}`} />
                </td>
                <td>{x.live_open}</td>
                <P v={x.pnl?.live?.today} id={`pnl-live-today-${x.kind}`} /><P v={x.pnl?.live?.week} id={`pnl-live-week-${x.kind}`} />
                <P v={x.pnl?.dry?.today} id={`pnl-dry-today-${x.kind}`} /><P v={x.pnl?.dry?.week} id={`pnl-dry-week-${x.kind}`} />
                <td><BudgetInput x={x} onSave={saveBudget} disabled={busy} /></td>
              </tr>
            ))}
            <tr className="border-t border-slate-700" data-testid="live-strategies-totals">
              <td className="!text-left font-semibold text-slate-300" colSpan={8}>All strategies</td>
              <P v={d.totals?.live?.today} id="pnl-live-today-total" /><P v={d.totals?.live?.week} id="pnl-live-week-total" />
              <P v={d.totals?.dry?.today} id="pnl-dry-today-total" /><P v={d.totals?.dry?.week} id="pnl-dry-week-total" />
              <td className="!text-right text-slate-500">cap −₹{num(d.hard_daily_loss, 0)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
